import { random, round2Str } from "../helpers/helpers";
import { aqiMeterRegistry } from "./aqiMeterRegistry";

// helpers
const formatTime = (h, m) =>
    `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

const formatDate = (date) =>
    `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;

export const generateAqiIndoorGetLatestDataDemo = ({ meter_id }) => {
    const now = new Date();

    // -------------------------
    // Resolve meter from registry
    // -------------------------
    const meterEntry = aqiMeterRegistry.find(
        (m) => m.meter_id === Number(meter_id)
    );

    if (!meterEntry) {
        throw new Error("AQI meter not found in registry");
    }

    const { meter_name, ranges } = meterEntry;

    // -------------------------
    // Live values
    // -------------------------
    const temperature = random(
        ranges.temperature[0],
        ranges.temperature[1]
    );
    const humidity = random(ranges.humidity[0], ranges.humidity[1]);
    const co = random(ranges.co[0], ranges.co[1]);
    const tvoc = random(ranges.tvoc[0], ranges.tvoc[1]);
    const formaldehyde = random(
        ranges.formaldehyde[0],
        ranges.formaldehyde[1]
    );
    const pm2 = random(ranges.pm2[0], ranges.pm2[1]);
    const pm10 = random(ranges.pm10[0], ranges.pm10[1]);

    // -------------------------
    // Daily stats
    // -------------------------
    const dailyMinTemp = temperature - random(0.5, 1.5);
    const dailyMaxTemp = temperature + random(0.5, 1.5);
    const dailyAvgTemp = (dailyMinTemp + dailyMaxTemp) / 2;

    const dailyMinHum = humidity - random(3, 7);
    const dailyMaxHum = humidity + random(3, 7);
    const dailyAvgHum = (dailyMinHum + dailyMaxHum) / 2;

    const dailyMinCo = co - random(100, 400);
    const dailyMaxCo = co + random(200, 800);
    const dailyAvgCo = (dailyMinCo + dailyMaxCo) / 2;

    const dailyMinPm2 = pm2 - random(5, 20);
    const dailyMaxPm2 = pm2 + random(5, 20);
    const dailyAvgPm2 = (dailyMinPm2 + dailyMaxPm2) / 2;

    const dailyMinPm10 = pm10 - random(10, 30);
    const dailyMaxPm10 = pm10 + random(10, 30);
    const dailyAvgPm10 = (dailyMinPm10 + dailyMaxPm10) / 2;

    // -------------------------
    // Monthly stats (past days only)
    // -------------------------
    const daysCompleted = now.getDate();

    const randomPastDate = () => {
        const d = new Date(now);
        d.setDate(random(1, daysCompleted));
        return formatDate(d);
    };

    const monthlyMinTemp = dailyMinTemp - random(0.5, 1.5);
    const monthlyMaxTemp = dailyMaxTemp + random(0.5, 2.0);
    const monthlyAvgTemp = (monthlyMinTemp + monthlyMaxTemp) / 2;

    const monthlyMinHum = dailyMinHum - random(4, 8);
    const monthlyMaxHum = dailyMaxHum + random(4, 8);
    const monthlyAvgHum = (monthlyMinHum + monthlyMaxHum) / 2;

    const monthlyMinCo = dailyMinCo - random(100, 300);
    const monthlyMaxCo = dailyMaxCo + random(300, 800);
    const monthlyAvgCo = (monthlyMinCo + monthlyMaxCo) / 2;

    const monthlyMinPm2 = dailyMinPm2 - random(5, 20);
    const monthlyMaxPm2 = dailyMaxPm2 + random(5, 30);
    const monthlyAvgPm2 = (monthlyMinPm2 + monthlyMaxPm2) / 2;

    const monthlyMinPm10 = dailyMinPm10 - random(10, 40);
    const monthlyMaxPm10 = dailyMaxPm10 + random(10, 50);
    const monthlyAvgPm10 = (monthlyMinPm10 + monthlyMaxPm10) / 2;

    // -------------------------
    // last_seen
    // -------------------------
    const lastSeen = `${String(now.getDate()).padStart(2, "0")}-${now.toLocaleString(
        "default",
        { month: "short" }
    )}-${now.getFullYear().toString().slice(-2)} ${String(
        now.getHours()
    ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

    return {
        // Identity
        sensor_name: meter_name,
        status: "online",

        // Live
        temperature: round2Str(temperature),
        humidity: round2Str(humidity),
        co: round2Str(co),
        tvoc: round2Str(tvoc),
        formaldehyde: round2Str(formaldehyde),
        pm2: round2Str(pm2),
        pm10: round2Str(pm10),
            "is_co": true,
            "is_formaldehyde": true,
            "is_humidity": true,
            "is_pm10": true,
            "is_pm2": true,
            "is_temperature": true,
            "is_tvoc": true,
        // Last seen
        last_seen: lastSeen,

        // Daily
        daily_min_temp: round2Str(dailyMinTemp),
        daily_max_temp: round2Str(dailyMaxTemp),
        daily_avg_temp: round2Str(dailyAvgTemp),
        daily_min_temp_time: formatTime(6, random(0, 59)),
        daily_max_temp_time: formatTime(14, random(0, 59)),

        daily_min_humidity: round2Str(dailyMinHum),
        daily_max_humidity: round2Str(dailyMaxHum),
        daily_avg_humidity: round2Str(dailyAvgHum),
        daily_min_humidity_time: formatTime(6, random(0, 59)),
        daily_max_humidity_time: formatTime(8, random(0, 59)),

        daily_min_co: round2Str(dailyMinCo),
        daily_max_co: round2Str(dailyMaxCo),
        daily_avg_co: round2Str(dailyAvgCo),
        daily_min_co_time: formatTime(14, random(0, 59)),
        daily_max_co_time: formatTime(6, random(0, 59)),

        daily_min_tvoc: round2Str(tvoc),
        daily_max_tvoc: round2Str(tvoc),
        daily_avg_tvoc: round2Str(tvoc),
        daily_min_tvoc_time: formatTime(0, random(0, 59)),
        daily_max_tvoc_time: formatTime(0, random(0, 59)),

        daily_min_formaldehyde: round2Str(formaldehyde),
        daily_max_formaldehyde: round2Str(formaldehyde),
        daily_avg_formaldehyde: round2Str(formaldehyde),
        daily_min_formaldehyde_time: formatTime(0, random(0, 59)),
        daily_max_formaldehyde_time: formatTime(0, random(0, 59)),

        // Monthly
        monthly_min_temp: round2Str(monthlyMinTemp),
        monthly_max_temp: round2Str(monthlyMaxTemp),
        monthly_avg_temp: round2Str(monthlyAvgTemp),
        monthly_min_temp_date: randomPastDate(),
        monthly_max_temp_date: randomPastDate(),

        monthly_min_humidity: round2Str(monthlyMinHum),
        monthly_max_humidity: round2Str(monthlyMaxHum),
        monthly_avg_humidity: round2Str(monthlyAvgHum),
        monthly_min_humidity_date: randomPastDate(),
        monthly_max_humidity_date: randomPastDate(),

        monthly_min_co: round2Str(monthlyMinCo),
        monthly_max_co: round2Str(monthlyMaxCo),
        monthly_avg_co: round2Str(monthlyAvgCo),
        monthly_min_co_date: randomPastDate(),
        monthly_max_co_date: randomPastDate(),

        monthly_min_tvoc: round2Str(tvoc),
        monthly_max_tvoc: round2Str(tvoc),
        monthly_avg_tvoc: round2Str(tvoc),
        monthly_min_tvoc_date: randomPastDate(),
        monthly_max_tvoc_date: randomPastDate(),

        monthly_min_formaldehyde: round2Str(formaldehyde),
        monthly_max_formaldehyde: round2Str(formaldehyde),
        monthly_avg_formaldehyde: round2Str(formaldehyde),
        monthly_min_formaldehyde_date: randomPastDate(),
        monthly_max_formaldehyde_date: randomPastDate(),

        // Daily PM2
        daily_min_pm2: round2Str(dailyMinPm2),
        daily_max_pm2: round2Str(dailyMaxPm2),
        daily_avg_pm2: round2Str(dailyAvgPm2),
        daily_min_pm2_time: formatTime(5, random(0, 59)),
        daily_max_pm2_time: formatTime(15, random(0, 59)),

        // Daily PM10
        daily_min_pm10: round2Str(dailyMinPm10),
        daily_max_pm10: round2Str(dailyMaxPm10),
        daily_avg_pm10: round2Str(dailyAvgPm10),
        daily_min_pm10_time: formatTime(5, random(0, 59)),
        daily_max_pm10_time: formatTime(15, random(0, 59)),

        // Monthly PM2
        monthly_min_pm2: round2Str(monthlyMinPm2),
        monthly_max_pm2: round2Str(monthlyMaxPm2),
        monthly_avg_pm2: round2Str(monthlyAvgPm2),
        monthly_min_pm2_date: randomPastDate(),
        monthly_max_pm2_date: randomPastDate(),

        // Monthly PM10
        monthly_min_pm10: round2Str(monthlyMinPm10),
        monthly_max_pm10: round2Str(monthlyMaxPm10),
        monthly_avg_pm10: round2Str(monthlyAvgPm10),
        monthly_min_pm10_date: randomPastDate(),
        monthly_max_pm10_date: randomPastDate(),
    };
};
